import { useIndicadorById, useVariablesIndicador, useHistorialIndicadores } from '@/hooks/useIndicadores';
import { indicadoresService, TipoResultadoIndicador } from '@/services/indicadores.service';
import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, TrendingUp, TrendingDown, Minus, Calendar, Info, ArrowLeft, ChevronDown, ChevronUp, X } from 'lucide-react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

function formatValor(v: number, tipo: TipoResultadoIndicador): string {
  if (tipo === 'MONEDA') {
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(v);
  }
  const formattedNum = Number.isFinite(v) ? parseFloat(v.toFixed(2)).toString() : '—';
  if (tipo === 'NUMERO') {
    return formattedNum;
  }
  return `${formattedNum}%`;
}

function getMonthName(month: number): string {
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return months[month - 1] || '';
}

function prepareChartData(historial: any[], indicadorId: number) {
  const filtered = historial.filter((h) => h.indicadorEmpresaId === indicadorId);
  const monthlyLatestMap = new Map<string, any>();
  filtered.forEach((h) => {
    const key = `${h.mes}-${h.anio}`;
    const existing = monthlyLatestMap.get(key);
    if (!existing || new Date(h.fechaRegistro).getTime() > new Date(existing.fechaRegistro).getTime()) {
      monthlyLatestMap.set(key, h);
    }
  });
  return Array.from(monthlyLatestMap.values())
    .sort((a, b) => (a.anio !== b.anio ? a.anio - b.anio : a.mes - b.mes))
    .map((h) => ({ name: `${getMonthName(h.mes)} ${h.anio}`, meta: h.valorMeta, actual: h.valorActual, mes: h.mes, anio: h.anio }));
}

function formatFrecuencia(frec: string): string {
  if (!frec) return '';
  const lower = frec.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function estadoChip(estado: string, ultimaEvaluacion?: string | null) {
  if (!ultimaEvaluacion) {
    return (
      <span
        className="admin-chip animate-pulse"
        style={{ background: 'var(--admin-surface2)', color: 'var(--admin-text2)', border: '1px solid var(--admin-border)' }}
      >
        Sin evaluar
      </span>
    );
  }
  const map: Record<string, { bg: string; color: string; label: string }> = {
    CUMPLE: { bg: 'var(--admin-green-bg)', color: 'var(--admin-green)', label: 'Cumple' },
    NO_CUMPLE: { bg: 'var(--admin-red-bg)', color: 'var(--admin-red)', label: 'No cumple' },
    PARCIAL: { bg: 'var(--admin-amber-bg)', color: 'var(--admin-amber)', label: 'Parcial' },
  };
  const s = map[estado] ?? { bg: 'var(--admin-surface2)', color: 'var(--admin-text2)', label: estado };
  return (
    <span className="admin-chip" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function frecuenciaChip(f: string) {
  return (
    <span className="admin-chip" style={{ background: 'var(--admin-blue-bg)', color: 'var(--admin-blue)' }}>
      {formatFrecuencia(f)}
    </span>
  );
}

export function IndicadorDetallePage() {
  const { nit, indicadorId } = useParams<{ nit: string; indicadorId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isFromAdmin = searchParams.get('from') === 'admin';
  const { data: indicador, isLoading, error } = useIndicadorById(nit ?? '', Number(indicadorId));
  const { data: variables } = useVariablesIndicador(nit ?? '', Number(indicadorId));
  const { data: historial, isLoading: isLoadingHistorial } = useHistorialIndicadores(nit ?? '');

  const [variableInputs, setVariableInputs] = useState<Record<number, string>>({});
  const [observaciones, setObservaciones] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showDetailedLogs, setShowDetailedLogs] = useState(false);
  const [activeTab, setActiveTab] = useState<'evaluar' | 'grafica'>('evaluar');

  useEffect(() => {
    if (variables) {
      const initialInputs: Record<number, string> = {};
      variables.forEach((v) => {
        initialInputs[v.id] = String(v.valor ?? 0);
      });
      setVariableInputs(initialInputs);
    }
  }, [variables]);

  useEffect(() => {
    if (indicador?.observaciones) setObservaciones(indicador.observaciones);
  }, [indicador]);

  const handleVariableChange = (variableId: number, value: string) => {
    if (value === '' || /^-?[0-9]*\.?[0-9]*$/.test(value)) {
      setVariableInputs((prev) => ({ ...prev, [variableId]: value }));
    }
  };

  // Real-time formula preview evaluation
  const realtimeFormula = useMemo(() => {
    if (!indicador || !variables || variables.length === 0) return { text: '', result: null };

    let mathFormula = indicador.formulaCalculo || '';
    if (!mathFormula) {
      mathFormula = variables.map((v) => `V${v.orden}`).join(' + ');
    }

    // Replace V1, V2, etc. with user input values
    const sortedByOrden = [...variables].sort((a, b) => (b.orden ?? 0) - (a.orden ?? 0));
    sortedByOrden.forEach((v) => {
      if (v.orden !== null) {
        const valStr = variableInputs[v.id] !== undefined && variableInputs[v.id] !== '' ? variableInputs[v.id] : '0';
        mathFormula = mathFormula.replace(new RegExp(`\\bV${v.orden}\\b`, 'g'), valStr);
      }
    });

    let result: number | null = null;
    try {
      if (/^[0-9\s\+\-\*\/\(\)\.]+$/.test(mathFormula)) {
        result = new Function(`return (${mathFormula})`)();
      }
    } catch (err) {
      console.error('Error calculating realtime formula:', err);
    }

    return {
      text: mathFormula,
      result,
    };
  }, [indicador, variables, variableInputs]);

  const handleSave = async () => {
    if (!nit) return;

    if (realtimeFormula.result !== null && (!Number.isFinite(realtimeFormula.result) || Number.isNaN(realtimeFormula.result))) {
      setSaveMessage({
        type: 'error',
        text: 'El cálculo del indicador genera una división por cero o un valor no válido.',
      });
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);
    try {
      await Promise.all(
        Object.entries(variableInputs).map(([id, val]) => {
          const numVal = parseFloat(val) || 0;
          return indicadoresService.actualizarVariableIndicador(nit, Number(id), numVal);
        })
      );
      if (indicadorId) {
        await indicadoresService.actualizarIndicadorEmpresa(nit, Number(indicadorId), observaciones || null);
      }
      setSaveMessage({ type: 'success', text: 'Variables y observaciones guardadas correctamente' });
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      setSaveMessage({ type: 'error', text: err instanceof Error ? err.message : 'Error al guardar los cambios' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="admin-content flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-(--admin-blue) border-t-transparent animate-spin" />
          <span className="text-sm text-(--admin-text2)">Cargando indicador...</span>
        </div>
      </div>
    );
  }

  if (error || !indicador) {
    return (
      <div className="admin-content text-center py-16">
        <AlertCircle className="w-10 h-10 text-(--admin-red) mx-auto mb-3" />
        <p className="text-(--admin-text2) mb-4">{error || 'Indicador no encontrado'}</p>
        <Link to={`/empresa/${nit}`}>
          <button className="admin-btn primary">Volver</button>
        </Link>
      </div>
    );
  }

  const tipo = indicador.tipoResultado ?? 'PORCENTAJE';
  const chartData = historial ? prepareChartData(historial, indicador.id) : [];

  return (
    <div className="admin-content">
      {/* Back */}
      <div className="flex items-center justify-between mb-5">
        <button
          className="admin-btn"
          onClick={() => navigate(isFromAdmin ? `/indicadores/asignaciones/${nit}` : `/empresa/${nit}/indicadores`)}
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Volver
        </button>
        {isFromAdmin && (
          <button
            type="button"
            onClick={() => navigate(`/indicadores/asignaciones/${nit}`)}
            className="p-1.5 rounded-lg text-(--admin-text3) hover:text-(--admin-text) hover:bg-(--admin-surface2) transition-all border border-(--admin-border) bg-white shadow-sm flex items-center gap-1 cursor-pointer"
            title="Cerrar detalle e ir a asignaciones"
          >
            <span style={{ fontFamily: 'Manrope, system-ui, sans-serif' }} className="text-[11px] font-medium text-gray-500">
              Cerrar
            </span>
            <X className="w-3.5 h-3.5 text-gray-500" />
          </button>
        )}
      </div>

      {/* Header */}
      <div className="admin-card mb-5">
        <div className="admin-card-header">
          <div className="flex-1 min-w-0">
            <h1 style={{ fontFamily: 'Manrope, system-ui, sans-serif', fontWeight: 700 }} className="text-xl text-(--admin-text) truncate">
              {indicador.nombre}
            </h1>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {frecuenciaChip(indicador.frecuencia)}
            {estadoChip(indicador.estado, indicador.ultimaEvaluacion)}
          </div>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-[11px] font-semibold text-(--admin-text3) uppercase tracking-wider mb-1.5">Fórmula</p>
            <p className="text-[13px] text-(--admin-text) font-mono bg-(--admin-surface2) px-3 py-2 rounded-lg border border-(--admin-border) mb-2">
              {indicador.formula}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-(--admin-surface2) rounded-lg border border-(--admin-border) px-4 py-3">
              <p className="text-[11px] font-semibold text-(--admin-text3) uppercase tracking-wider mb-1">Meta</p>
              <p style={{ fontFamily: 'Manrope, system-ui, sans-serif', fontWeight: 700 }} className="text-xl text-(--admin-text)">
                {formatValor(indicador.meta, tipo)}
              </p>
            </div>
            <div className="bg-(--admin-blue-bg) rounded-lg border border-(--admin-border) px-4 py-3">
              <p className="text-[11px] font-semibold text-(--admin-blue) uppercase tracking-wider mb-1">Resultado</p>
              <p style={{ fontFamily: 'Manrope, system-ui, sans-serif', fontWeight: 700 }} className="text-xl text-(--admin-blue)">
                {!indicador.ultimaEvaluacion ? '—' : formatValor(indicador.resultado, tipo)}
              </p>
            </div>
          </div>
        </div>

        {indicador.fuenteDatos && (
          <div className="px-4 pb-4">
            <p className="text-[11px] font-semibold text-(--admin-text3) uppercase tracking-wider mb-1">Fuente de datos</p>
            <p className="text-[13px] text-(--admin-text2)">{indicador.fuenteDatos}</p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-4">
        <div className="admin-tab-bar" style={{ marginBottom: 0 }}>
          <button className={`admin-tab ${activeTab === 'evaluar' ? 'active' : ''}`} onClick={() => setActiveTab('evaluar')}>
            Evaluar
          </button>
          <button className={`admin-tab ${activeTab === 'grafica' ? 'active' : ''}`} onClick={() => setActiveTab('grafica')}>
            Gráfica
          </button>
        </div>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'evaluar' && (
          <div className="space-y-5">
            {/* Variables */}
            <div className="admin-card">
              <div className="admin-card-header">
                <h2 style={{ fontFamily: 'Manrope, system-ui, sans-serif', fontWeight: 600 }} className="text-sm text-(--admin-text)">
                  Variables del indicador
                </h2>
              </div>

              {variables && variables.length > 0 ? (
                <div className="p-4 space-y-4">
                  <div className="space-y-3">
                    {variables
                      .sort((a, b) => (a.orden || 0) - (b.orden || 0))
                      .map((variable) => (
                        <div key={variable.id} className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <label htmlFor={`var-${variable.id}`} className="flex-1 text-[13px] text-(--admin-text)">
                            {variable.orden !== null && (
                              <span className="text-[11px] text-(--admin-text3) mr-2 font-mono">#{variable.orden}</span>
                            )}
                            {variable.nombre}
                          </label>
                          <input
                            id={`var-${variable.id}`}
                            type="text"
                            inputMode="decimal"
                            value={variableInputs[variable.id] ?? ''}
                            onChange={(e) => handleVariableChange(variable.id, e.target.value)}
                            disabled={isFromAdmin}
                            className="admin-search-input w-full sm:w-44 pl-3 focus:border-(--admin-blue) focus:ring-1 focus:ring-(--admin-blue) disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                            placeholder="0"
                          />
                        </div>
                      ))}
                  </div>

                  {/* Real-time formula preview inside variables card */}
                  {realtimeFormula && (
                    <div
                      className="mt-4 p-4 rounded-xl border transition-all duration-200"
                      style={{
                        background: 'var(--admin-blue-bg)',
                        borderColor: 'rgba(30, 95, 168, 0.15)',
                      }}
                    >
                      <p className="font-semibold text-(--admin-blue) text-xs mb-1.5">Cálculo en tiempo real:</p>
                      <div className="font-mono text-(--admin-text) bg-white/70 px-3 py-2 rounded-lg border border-gray-100 text-[12px] break-all mb-2 leading-relaxed">
                        {realtimeFormula.text}
                      </div>
                      <div className="text-gray-800 text-[13px] flex items-center justify-between">
                        <span>Resultado proyectado:</span>
                        <span className="font-bold text-sm text-(--admin-blue) bg-white px-2.5 py-0.5 rounded-full border border-gray-100 shadow-sm">
                          {formatValor(realtimeFormula.result ?? 0, tipo)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="px-4 py-6 text-[13px] text-(--admin-text3)">No hay variables definidas para este indicador.</p>
              )}
            </div>

            {/* Observaciones */}
            <div className="admin-card">
              <div className="admin-card-header">
                <h2 style={{ fontFamily: 'Manrope, system-ui, sans-serif', fontWeight: 600 }} className="text-sm text-(--admin-text)">
                  Observaciones
                </h2>
              </div>
              <div className="p-4">
                <textarea
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  disabled={isFromAdmin}
                  className="w-full px-3 py-2 rounded-lg border border-(--admin-border2) bg-(--admin-surface) text-[13px] text-(--admin-text)
                    focus:outline-2 focus:outline-(--admin-blue) focus:outline-offset-1
                    placeholder:text-(--admin-text3) resize-y min-h-24 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                  placeholder="Agregar observaciones sobre este indicador..."
                />
              </div>
            </div>

            {/* Save feedback */}
            {saveMessage && (
              <div
                className="mb-4 px-4 py-3 rounded-lg text-[13px]"
                style={{
                  background: saveMessage.type === 'success' ? 'var(--admin-green-bg)' : 'var(--admin-red-bg)',
                  color: saveMessage.type === 'success' ? 'var(--admin-green)' : 'var(--admin-red)',
                  border: `1px solid ${saveMessage.type === 'success' ? 'var(--admin-green)' : 'var(--admin-red)'}22`,
                }}
              >
                {saveMessage.text}
              </div>
            )}

            {/* Save button */}
            {!isFromAdmin && (
              <div className="mb-8">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="admin-btn primary w-full justify-center py-2.5 text-[13px] font-medium hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-50"
                >
                  {isSaving ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'grafica' && (
          <div className="space-y-5">
            {/* Historical chart */}
            <div className="admin-card">
              <div className="admin-card-header">
                <TrendingUp className="w-4 h-4 text-(--admin-blue)" />
                <h2 style={{ fontFamily: 'Manrope, system-ui, sans-serif', fontWeight: 600 }} className="text-sm text-(--admin-text)">
                  Evolución mensual
                </h2>
              </div>

              {isLoadingHistorial ? (
                <div className="flex justify-center py-12">
                  <div className="w-6 h-6 rounded-full border-2 border-(--admin-blue) border-t-transparent animate-spin" />
                </div>
              ) : chartData.length > 0 ? (
                <div className="p-4 space-y-6">
                  {/* Chart */}
                  <div className="bg-(--admin-surface2) rounded-xl border border-(--admin-border) p-4">
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
                        <XAxis dataKey="name" stroke="var(--admin-text3)" style={{ fontSize: '11px' }} />
                        <YAxis stroke="var(--admin-text3)" style={{ fontSize: '11px' }} />
                        <Tooltip
                          formatter={(value: any, name: any) => [formatValor(Number(value), tipo), name]}
                          contentStyle={{
                            background: 'var(--admin-surface)',
                            border: '1px solid var(--admin-border)',
                            borderRadius: 'var(--admin-r)',
                            fontSize: '12px',
                            padding: '8px 12px',
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px', paddingTop: 10 }} />
                        <Line
                          type="monotone"
                          dataKey="meta"
                          stroke="var(--admin-border2)"
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          name="Meta"
                          dot={{ fill: 'var(--admin-border2)', r: 3 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="actual"
                          stroke="var(--admin-blue)"
                          strokeWidth={2.5}
                          name="Valor actual"
                          dot={{ fill: 'var(--admin-blue)', r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Monthly summary table */}
                  <div>
                    <p
                      style={{ fontFamily: 'Manrope, system-ui, sans-serif', fontWeight: 600 }}
                      className="text-[13px] text-(--admin-text) mb-3"
                    >
                      Resumen mensual
                    </p>
                    <div className="overflow-x-auto">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Período</th>
                            <th className="text-center">Meta</th>
                            <th className="text-center">Valor de cierre</th>
                            <th className="text-center">Variación</th>
                            <th className="text-center">Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {chartData.map((item, index) => {
                            const cumpleMeta = item.meta < 0 ? item.actual <= item.meta : item.actual >= item.meta;
                            const prevVal = index > 0 ? chartData[index - 1].actual : null;
                            const diff = prevVal !== null ? item.actual - prevVal : null;

                            return (
                              <tr key={index}>
                                <td className="text-(--admin-text) font-medium">{item.name}</td>
                                <td className="text-center text-(--admin-text2)">{formatValor(item.meta, tipo)}</td>
                                <td className="text-center font-semibold text-(--admin-text)">{formatValor(item.actual, tipo)}</td>
                                <td className="text-center">
                                  {diff === null ? (
                                    <span className="text-(--admin-text3)">—</span>
                                  ) : diff > 0 ? (
                                    <span
                                      className="admin-chip"
                                      style={{ background: 'var(--admin-green-bg)', color: 'var(--admin-green)' }}
                                    >
                                      <TrendingUp className="w-3 h-3" />+{diff.toFixed(2)}
                                      {tipo === 'PORCENTAJE' ? '%' : ''}
                                    </span>
                                  ) : diff < 0 ? (
                                    <span className="admin-chip" style={{ background: 'var(--admin-red-bg)', color: 'var(--admin-red)' }}>
                                      <TrendingDown className="w-3 h-3" />
                                      {diff.toFixed(2)}
                                      {tipo === 'PORCENTAJE' ? '%' : ''}
                                    </span>
                                  ) : (
                                    <span
                                      className="admin-chip"
                                      style={{ background: 'var(--admin-surface2)', color: 'var(--admin-text3)' }}
                                    >
                                      <Minus className="w-3 h-3" />
                                      0.00
                                    </span>
                                  )}
                                </td>
                                <td className="text-center">{estadoChip(cumpleMeta ? 'CUMPLE' : 'NO_CUMPLE', 'evaluated')}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Detailed history collapsible */}
                  <div className="border-t border-(--admin-border) pt-4">
                    <button
                      className="w-full flex items-center justify-between text-left"
                      onClick={() => setShowDetailedLogs(!showDetailedLogs)}
                    >
                      <div className="flex items-center gap-2">
                        <Info className="w-4 h-4 text-(--admin-blue)" />
                        <span
                          style={{ fontFamily: 'Manrope, system-ui, sans-serif', fontWeight: 600 }}
                          className="text-[13px] text-(--admin-text)"
                        >
                          Historial detallado de ediciones
                        </span>
                      </div>
                      {showDetailedLogs ? (
                        <ChevronUp className="w-4 h-4 text-(--admin-text3)" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-(--admin-text3)" />
                      )}
                    </button>
                    <p className="text-[11px] text-(--admin-text3) mt-0.5 ml-6">Trazabilidad de todas las modificaciones registradas.</p>

                    {showDetailedLogs && (
                      <div className="mt-4 overflow-x-auto">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Fecha y hora</th>
                              <th className="text-center">Meta</th>
                              <th className="text-center">Valor registrado</th>
                              <th className="text-center">Período</th>
                            </tr>
                          </thead>
                          <tbody>
                            {historial
                              .filter((h) => h.indicadorEmpresaId === indicador.id)
                              .sort((a, b) => new Date(b.fechaRegistro).getTime() - new Date(a.fechaRegistro).getTime())
                              .map((item, index) => (
                                <tr key={index}>
                                  <td className="flex items-center gap-1.5 text-(--admin-text)">
                                    <Calendar className="w-3.5 h-3.5 text-(--admin-text3)" />
                                    {new Date(item.fechaRegistro).toLocaleString('es-CO', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </td>
                                  <td className="text-center text-(--admin-text2)">{formatValor(item.valorMeta, tipo)}</td>
                                  <td className="text-center font-semibold text-(--admin-blue)">{formatValor(item.valorActual, tipo)}</td>
                                  <td className="text-center text-(--admin-text3)">
                                    {getMonthName(item.mes)} {item.anio}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center py-12 gap-2">
                  <AlertCircle className="w-9 h-9 text-(--admin-border2)" />
                  <p className="text-[13px] text-(--admin-text3)">No hay datos históricos para este indicador.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
